// Product images are grouped by their alt-text prefix. No variant fetch is needed.
const normalize = (value = '') => value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
export function imageGroup(alt = '') {
  const label = alt.split('|')[0].trim();
  if (normalize(label) === 'display') return 'display';
  const match = label.match(/^colou?r\s*:\s*(.+)$/i);
  return match ? normalize(match[1]) : '';
}
export function chooseImages(images, color, fallbackId) {
  const matches = images.filter((image) => imageGroup(image.alt) === normalize(color));
  if (matches.length) return matches;
  const fallback = images.find((image) => String(image.id) === String(fallbackId));
  return fallback ? [fallback] : [];
}

class GoodDawgColorGallery extends HTMLElement {
  connectedCallback() {
    queueMicrotask(() => { if (this.isConnected) this.initialize(); });
  }

  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.items = [...this.querySelectorAll('[data-gd-media]')];
    this.thumbnails = [...this.querySelectorAll('[data-gd-thumbnail]')];
    this.variants = JSON.parse(this.querySelector('[data-gd-variants]').textContent);
    this.images = this.items.map((item) => ({ id: item.dataset.id, alt: item.dataset.alt }));
    this.activeItems = [];
    this.index = 0;
    this.color = null;
    this.showDisplay();

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-gd-thumbnail')) {
        this.select(this.activeItems.findIndex((item) => item.dataset.id === button.dataset.id));
      } else if (button.hasAttribute('data-gd-prev')) this.select(this.index - 1);
      else if (button.hasAttribute('data-gd-next')) this.select(this.index + 1);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      this.thumbnails.find((button) => button.dataset.id === this.activeItems[this.index]?.dataset.id)?.focus();
    }, { signal });

    const section = this.closest('.shopify-section') || this.parentElement;
    this.prepareColorChoice(section);
    const handleOption = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const picker = target.closest('variant-picker');
      if (!picker || picker.dataset.productId !== this.dataset.productId) return;
      const option = target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      if (!option?.matches('input[type="radio"], option')) return;
      const optionName = normalize(option.dataset.optionName);
      const configuredName = normalize(this.dataset.colorOption);
      if (optionName !== configuredName && !(configuredName === 'color' && optionName === 'colour')) {
        if (this.waitingForColor && event.type === 'change') {
          // Keep a size choice, but do not fetch a default color before one is chosen.
          picker.updateSelectedOption?.(target);
          event.stopImmediatePropagation();
        }
        return;
      }
      if (!option.value) return;
      this.releaseColorChoice();
      // Clicking the already-selected color must also leave the display image.
      this.showColor(option.value);
    };
    section.addEventListener('change', handleOption, { signal, capture: true });
    section.addEventListener('click', handleOption, { signal, capture: true });
    section.addEventListener('submit', (event) => {
      if (!this.waitingForColor) return;
      const form = event.target.closest('product-form-component');
      if (form?.dataset.productId !== this.dataset.productId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.choicePrompt?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, { signal, capture: true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.releaseColorChoice();
  }

  isColorOption(option) {
    const name = normalize(option.dataset.optionName);
    const configured = normalize(this.dataset.colorOption);
    return name === configured || (configured === 'color' && name === 'colour');
  }

  prepareColorChoice(section) {
    this.releaseColorChoice();
    const picker = [...section.querySelectorAll('variant-picker')].find((el) => el.dataset.productId === this.dataset.productId);
    if (!picker) return;
    const options = [...picker.querySelectorAll('input[type="radio"], option')].filter((option) => this.isColorOption(option));
    if (!options.length) return;
    this.waitingForColor = true;
    for (const option of options) {
      if (option.tagName === 'INPUT') {
        option.checked = false;
        option.removeAttribute('checked');
        option.dataset.currentChecked = 'false';
        option.dataset.previousChecked = 'false';
      } else {
        option.selected = false;
        option.removeAttribute('selected');
      }
    }
    const select = options.find((option) => option.tagName === 'OPTION')?.parentElement;
    if (select) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Choose a color';
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.dataset.gdPlaceholder = '';
      select.prepend(placeholder);
      select.value = '';
      this.choicePlaceholder = placeholder;
    }
    this.choicePrompt = document.createElement('p');
    this.choicePrompt.className = 'gd-gallery__choice-prompt';
    this.choicePrompt.textContent = 'Choose a color to see its photos and add to cart.';
    picker.before(this.choicePrompt);
    this.lockedForms = [];
    this.lockedButtons = [];
    for (const form of section.querySelectorAll('product-form-component, sticky-add-to-cart')) {
      if (form.dataset.productId !== this.dataset.productId) continue;
      this.lockedForms.push({ form, inert: form.inert });
      form.inert = true;
      form.classList.add('gd-awaiting-color');
      for (const button of form.querySelectorAll('button[type="submit"], button[name="add"], .sticky-add-to-cart__button')) {
        this.lockedButtons.push({ button, disabled: button.disabled });
        button.disabled = true;
      }
    }
  }

  releaseColorChoice() {
    this.waitingForColor = false;
    this.choicePrompt?.remove();
    this.choicePlaceholder?.remove();
    this.choicePrompt = null;
    this.choicePlaceholder = null;
    for (const { form, inert } of this.lockedForms || []) {
      form.inert = inert;
      form.classList.remove('gd-awaiting-color');
    }
    for (const { button, disabled } of this.lockedButtons || []) button.disabled = disabled;
    this.lockedForms = [];
    this.lockedButtons = [];
  }

  showDisplay() {
    const display = this.items.find((item) => imageGroup(item.dataset.alt) === 'display') || this.items[0];
    this.setItems(display ? [display] : []);
  }

  showColor(color) {
    const key = normalize(color);
    // A second event from the same click should not reset the gallery.
    if (this.color === key) return;
    this.color = key;
    const variant = this.variants.find((variant) => normalize(variant.options[Number(this.dataset.colorPosition) - 1]) === key && variant.image_id);
    const selected = chooseImages(this.images, key, variant?.image_id);
    const ids = new Set(selected.map((image) => String(image.id)));
    const items = this.items.filter((item) => ids.has(item.dataset.id));
    this.setItems(items.length ? items : this.items.filter((item) => imageGroup(item.dataset.alt) === 'display').slice(0, 1));
    if (!this.activeItems.length) this.showDisplay();
    const status = this.querySelector('[data-gd-status]');
    status.textContent = items.length ? `${color}: ${items.length} photo${items.length === 1 ? '' : 's'}.` : `No photos labeled for ${color} yet. Showing the display image.`;
  }

  setItems(items) {
    this.activeItems = items;
    const ids = new Set(items.map((item) => item.dataset.id));
    this.thumbnails.forEach((button) => { button.hidden = !ids.has(button.dataset.id); });
    this.querySelector('[data-gd-thumbnails]').hidden = items.length < 2;
    this.querySelectorAll('[data-gd-prev], [data-gd-next]').forEach((button) => { button.hidden = items.length < 2; });
    this.select(0);
  }

  select(index) {
    if (!this.activeItems.length || index < -1) return;
    this.index = (index + this.activeItems.length) % this.activeItems.length;
    const active = this.activeItems[this.index];
    this.items.forEach((item) => { item.hidden = item !== active; });
    this.thumbnails.forEach((button) => {
      const selected = button.dataset.id === active.dataset.id;
      button.setAttribute('aria-current', selected ? 'true' : 'false');
    });
  }
}

if (!customElements.get('gd-color-gallery')) customElements.define('gd-color-gallery', GoodDawgColorGallery);
